import { useState } from 'react';
import { Link } from 'react-router';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { JsonLd, breadcrumbJsonLd } from '@/components/JsonLd';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { blogPosts } from '@/data/blog-posts';
import { formatBlogDate } from '@/lib/utils';

const categories = ['All', 'Engineering', 'Product', 'Guides'] as const;

const categoryColors: Record<string, string> = {
  Engineering:
    'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  Product:
    'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  Guides:
    'bg-amber-500/10 text-amber-400 border border-amber-500/20',
};

function AuthorAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('');
  return (
    <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-xs font-bold text-white shrink-0">
      {initials}
    </div>
  );
}

export function BlogPage() {
  useDocumentMeta({ title: 'Blog — LogPulse', description: 'Engineering insights, product updates, and observability best practices from the LogPulse team.' });

  const [activeCategory, setActiveCategory] = useState<string>('All');

  useDocumentHead({
    title: 'Blog',
    description:
      'Engineering insights, product updates, and observability best practices from the LogPulse team.',
    url: '/blog',
  });

  const filtered =
    activeCategory === 'All'
      ? blogPosts
      : blogPosts.filter((p) => p.category === activeCategory);

  // CollectionPage JSON-LD for blog listing
  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'LogPulse Blog',
    description:
      'Engineering insights, product updates, and observability best practices from the LogPulse team.',
    url: 'https://logpulse.io/blog',
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: 'https://logpulse.io',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: blogPosts.map((post, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://logpulse.io/blog/${post.slug}`,
        name: post.title,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <LandingHeader />

      <JsonLd data={collectionJsonLd} />
      <JsonLd
        data={breadcrumbJsonLd([
          { name: 'Home', url: 'https://logpulse.io' },
          { name: 'Blog', url: 'https://logpulse.io/blog' },
        ])}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            LogPulse Blog
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Engineering insights, product updates, and observability best practices
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="flex gap-2 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === cat
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Blog Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {filtered.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group block rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-6 hover:border-primary-500/50 dark:hover:border-primary-500/40 transition-colors"
            >
              {/* Category Badge */}
              <span
                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mb-4 ${categoryColors[post.category]}`}
              >
                {post.category}
              </span>

              {/* Title */}
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-500 transition-colors">
                {post.title}
              </h2>

              {/* Excerpt */}
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2 mb-6">
                {post.excerpt}
              </p>

              {/* Author Row */}
              <div className="flex items-center gap-3">
                <AuthorAvatar name={post.author} />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {post.author}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {formatBlogDate(post.date)} &middot; {post.readTime} read
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
